import {
  Component,
  AfterViewInit,
  ChangeDetectionStrategy,
  OnDestroy,
  computed,
  inject,
  Signal,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipDirective } from '../../../../../shared/components/ui/tooltip/tooltip.directive';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRightFromArc,
  faArrowLeftToArc,
  faHashtag,
  faHashtagLock,
} from '@fortawesome/pro-duotone-svg-icons';

import { UserEntry } from '../user-entry/user-entry';
import { User, UserStore } from '../../../../../state/user.repository';
import { UserService } from '../../../../services/user.service';
import { AppService } from '../../../../services/app.service';
import { UserSettingsStore } from '../../../../../state/user-settings.repository';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

type ListItem = { type: 'header'; roomId: string } | { type: 'user'; user: User };

@Component({
  selector: 'andor-users-list',
  standalone: true,
  imports: [
    CommonModule,
    TooltipDirective,
    TranslatePipe,
    UserEntry,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
    CdkFixedSizeVirtualScroll,
    FontAwesomeModule,
  ],
  templateUrl: './users-list.html',
  styleUrl: './users-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersList implements AfterViewInit, OnDestroy {
  userStore = inject(UserStore);
  Array = Array;
  protected readonly faArrowLeftToArc = faArrowLeftToArc;
  protected readonly faArrowRightFromArc = faArrowRightFromArc;
  protected readonly faHashtag = faHashtag;
  protected readonly faHashtagLock = faHashtagLock; // TODO

  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  // 54px tile + 6px gap. The fixed-size strategy places rows at
  // `index * itemSize` and never measures them, so the gap lives inside each
  // slot (padding on the entry host) rather than as a margin between rows.
  // Bound to `--list-item-height` so the stylesheets read this value.
  readonly itemSize = 60;
  lastBreakoutRoomId = signal<string | null>(null);

  private resizeObserver: ResizeObserver | null = null;

  flattenedItems: Signal<ListItem[]> = toSignal(
    this.userStore.userListUsers$.pipe(
      map((users) => {
        // Group users by breakoutRoomId
        const roomsMap = new Map<string, User[]>();
        for (const user of users) {
          const id = user.data.breakoutRoomId ?? '';
          const existingUsers = roomsMap.get(id);
          if (existingUsers) {
            existingUsers.push(user);
          } else {
            roomsMap.set(id, [user]);
          }
        }

        const sortedRooms = Array.from(roomsMap.entries()).sort(([a], [b]) => a.localeCompare(b));

        // Flatten into a single array: headers and users
        const flattened: ListItem[] = [];
        for (const [roomId, roomUsers] of sortedRooms) {
          // Add header only if roomId is not empty
          if (roomId) {
            flattened.push({ type: 'header', roomId });
          }
          // Add all users in this room
          for (const user of roomUsers) {
            flattened.push({ type: 'user', user });
          }
        }
        return flattened;
      })
    ),
    { initialValue: [] }
  );

  // Precomputed overlay heights per roomId so the template doesn't walk
  // `flattenedItems()` on every CD pass per header (O(N²) before).
  roomOverlayHeights = computed<Map<string, number>>(() => {
    const items = this.flattenedItems();
    const counts = new Map<string, number>();
    let currentRoom: string | null = null;
    for (const item of items) {
      if (item.type === 'header') {
        currentRoom = item.roomId;
        counts.set(currentRoom, 0);
      } else if (currentRoom !== null) {
        counts.set(currentRoom, (counts.get(currentRoom) ?? 0) + 1);
      }
    }
    const heights = new Map<string, number>();
    for (const [roomId, count] of counts) {
      heights.set(roomId, this.itemSize + count * this.itemSize);
    }
    return heights;
  });

  constructor(
    public userService: UserService,
    public appService: AppService,
    public userSettingsStore: UserSettingsStore
  ) {}

  ngAfterViewInit(): void {
    if (this.viewport) {
      // Observe viewport size changes (e.g. from window resize or side panel animation)
      this.resizeObserver = new ResizeObserver(() => {
        this.viewport?.checkViewportSize();
      });
      this.resizeObserver.observe(this.viewport.elementRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  trackByItem(index: number, item: ListItem): string {
    if (item.type === 'header') {
      return `header-${item.roomId}`;
    }
    return `user-${item.user.id}`;
  }

  openSidePanel(): void {
    this.userSettingsStore.update({ usersListExpanded: true });
  }

  joinBreakoutRoom(roomId: string) {
    this.userService.updateUserData({ breakoutRoomId: roomId });
  }

  leaveBreakoutRoom(roomId: string) {
    this.lastBreakoutRoomId.set(roomId);
    this.userService.updateUserData({ breakoutRoomId: '' });
  }

  scrollToRoom(roomId: string): void {
    if (!this.viewport || !roomId) return;

    const items = this.flattenedItems();
    const headerIndex = items.findIndex((item) => item.type === 'header' && item.roomId === roomId);

    if (headerIndex !== -1) {
      this.viewport.scrollToIndex(headerIndex, 'smooth');
    }
  }
}
